import { OrderStatus } from '@/__generated__/graphql';
import { useQuery } from '@apollo/client';
import { ActionIcon, Collapse, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { liveOrdersSummaryDocument } from '../graphql/queries';
import { useLocalStorage } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

export const OrdersSummary = ({ liveId }: { liveId: string }) => {
  const [opened, setOpened] = useLocalStorage({ key: 'orders-summary', defaultValue: false });

  const { data, loading } = useQuery(liveOrdersSummaryDocument, {
    variables: {
      where: {
        liveId: { equals: liveId },
        total: { gt: 0 },
        status: { notIn: [OrderStatus.Cancelled, OrderStatus.Internal] },
      },
    },
    skip: !opened,
  });

  const s = data?.liveOrdersSummary;

  return (
    <Paper p="md" withBorder>
      <Group justify="end">
        <Text size="sm" c="dimmed">
          {opened ? 'Skryť' : 'Zobraziť'} štatistiky
        </Text>
        <ActionIcon onClick={() => setOpened((prev) => !prev)} variant="subtle">
          {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        </ActionIcon>
      </Group>
      <Collapse in={opened}>
        {loading || !s ? (
          <Loader />
        ) : (
          <Group justify="space-between">
            <Stack gap={0}>
              <Text>
                Všetky objednávky: {s.all.count} ({s.all.total} €)
              </Text>
              <Text>
                z toho prevody: {s.bank.count} ({s.bank.total} €)
              </Text>
              <Text>
                z toho linky: {s.card.count} ({s.card.total} €)
              </Text>
              <Text>
                z toho dobierky: {s.cod.count} ({s.cod.total} €)
              </Text>
              <Text>
                z toho osobný: {s.cash.count} ({s.cash.total} €)
              </Text>
            </Stack>
            <Stack gap={0}>
              <Text c={s.unpaidBank.count > 0 ? 'red' : undefined}>
                Nezaplatené prevody: {s.unpaidBank.count} ({s.unpaidBank.total} €)
              </Text>
              <Text c={s.unpaidCard.count > 0 ? 'red' : undefined}>
                Nezaplatené linky: {s.unpaidCard.count} ({s.unpaidCard.total} €)
              </Text>
              <Text c={s.unpaidCods.count > 0 ? 'red' : undefined}>
                Nezaplatené dobierky: {s.unpaidCods.count} ({s.unpaidCods.total} €)
              </Text>
              <Text c={s.processed.count > 0 ? 'teal' : 'red'}>
                Na osobnom: {s.processed.count} ({s.processed.total} €)
              </Text>
              <Text c={s.shipped.count > 0 ? 'teal' : 'red'}>
                Odoslané: {s.shipped.count} ({s.shipped.total} €)
              </Text>
            </Stack>
            <Stack gap={0}>
              <Text fw={500} c={s.created.count > 0 ? 'red' : 'teal'}>
                Vytvorené: {s.created.count} ({s.created.total} €)
              </Text>
              <Text fw={500} c={s.confirmed.count > 0 ? 'red' : 'teal'}>
                Potvrdené: {s.confirmed.count} ({s.confirmed.total} €)
              </Text>
              <Text c={s.reviewing.count > 0 ? 'red' : 'teal'}>
                Problémové: {s.reviewing.count} ({s.reviewing.total} €)
              </Text>
              <Text c={s.paymentProblems.count > 0 ? 'red' : 'teal'}>
                Link zlyhal: {s.paymentProblems.count} ({s.paymentProblems.total} €)
              </Text>
              <Text c={s.delivered.count > 0 ? 'teal' : 'red'}>
                Doručené: {s.delivered.count} ({s.delivered.total} €)
              </Text>
            </Stack>
            <Stack gap={0}>
              {s.packed.count > 0 && (
                <Text c="teal">
                  Balia sa: {s.packed.count} ({s.packed.total} €)
                </Text>
              )}
              {s.reserved.count > 0 && (
                <Text c="orange">
                  Rezervované: {s.reserved.count} ({s.reserved.total} €)
                </Text>
              )}
              <Text fw={500} c={s.leftPersonal.count > 0 ? 'red' : 'teal'}>
                Osobný: {s.leftPersonal.count} ({s.leftPersonal.total} €)
              </Text>
              <Text fw={500} c={s.leftCods.count > 0 ? 'red' : 'teal'}>
                Dobierky: {s.leftCods.count} ({s.leftCods.total} €)
              </Text>
              <Text fw={500} c={s.leftBank.count > 0 ? 'red' : 'teal'}>
                Prevody: {s.leftBank.count} ({s.leftBank.total} €)
              </Text>
              <Text fw={500} c={s.leftCard.count > 0 ? 'red' : 'teal'}>
                Linky: {s.leftCard.count} ({s.leftCard.total} €)
              </Text>
            </Stack>
          </Group>
        )}
      </Collapse>
    </Paper>
  );
};
